import { EventEmitter, Injectable, Renderer2 } from '@angular/core';
import { MockupsUploadResponse, UploadDesignPositionMockupsResponse } from '@core/models/catalog';
import { DesignToolCanvas, Designs, Dimensions, PositionDesign, VariantColors, VariantImages } from '@core/models/designTool';
import { fabric } from 'fabric';
import { firstValueFrom } from 'rxjs';
import { CatalogDesignService } from '../catalog/catalog-design.service';
import { Store } from '@ngrx/store';
import { selectMobileView } from '@core/state/design-tool/design-tool.selectors';
import { IObjectOptions } from 'fabric/fabric-impl';

@Injectable({
  providedIn: 'root'
})
export class CanvasService {
  canvas: fabric.Canvas|null;
  rotationEnded = new EventEmitter<fabric.Image>();
  objectDeleted = new EventEmitter<string>();
  objectLocked = new EventEmitter<string>();
  objectEdited = new EventEmitter<string>();
  private rotating: boolean = false;
  private initialAngle: number = 0;
  private objectInitialAngle: number = 0;
  productImageClientWidth: number; // the current position rendered product image width
  productImageClientHeight: number;

  constructor(private catalogDesignService: CatalogDesignService, private store: Store) { }

  /**
   * Initializes the canvas with given id
   */
  initializeCanvas(canvasId: string, dimensions: Dimensions, onMoveCallback: (image) => {}, onScaleCallback: (image) => {}): boolean {
    if (!dimensions) {
      console.error('no dimensions received')
      return false
    }
    if (this.canvas) {
      this.disposeCanvas()
    }
    this.canvas = new fabric.Canvas(canvasId, {width: dimensions.width, height: dimensions.height});
    this.addDeleteControl();
    this.addRotationControl();

    this.canvas.on('object:modified', (e) => {
      if (e.target.type === 'image') {
        const activeObject = e.target as fabric.Image;
        if (activeObject && activeObject.type === 'image') {
          onMoveCallback(activeObject);
        }
      }
    })

    this.canvas.on('object:scaled', (e) => {
      const activeObject = e.target as fabric.Image;
      if (activeObject && activeObject.type === 'image') {
        onScaleCallback(activeObject);
      }
    })
    return true
  }

  async setCanvasStyles(element: HTMLElement, renderer: Renderer2, dimensions, borderColor) {
    if (!dimensions) {
      console.error('no dimensions received')
      return
    }
    this.canvas.setWidth(dimensions.width)
    this.canvas.setHeight(dimensions.height)
    const mobileView = await firstValueFrom(this.store.select(selectMobileView))
    renderer.setStyle(element, 'border', `${mobileView ? 1.5 : 3}px solid ${borderColor}`);
    renderer.setStyle(element, 'top', `${dimensions.top}px`);
    renderer.setStyle(element, 'left', `${dimensions.left}px`);
    this.drawTicTacToeGrid(borderColor, mobileView);
  }

  drawTicTacToeGrid(borderColor = "#fff", mobileView: boolean) {
    const canvas = this.canvas;
    if (!canvas) return;

    const quarterWidth = canvas.getWidth() / 4;
    const quarterHeight = canvas.getHeight() / 4;
    const lineWidthAdjustment = mobileView ? 1.5 : 3; // Adjust this value as needed

    // Draw vertical lines
    for (let i = 1; i <= 3; i++) {
        const xPos = quarterWidth * i;
        const verticalLine = new fabric.Line([xPos, 0, xPos, canvas.getHeight() - lineWidthAdjustment], {
            stroke: borderColor,
            strokeWidth: mobileView ? .5 : 1,
            selectable: false,
            evented: false
        });
        canvas.add(verticalLine);
    }

    // Draw horizontal lines
    for (let i = 1; i <= 3; i++) {
        const yPos = quarterHeight * i;
        const horizontalLine = new fabric.Line([0, yPos, canvas.getWidth() - lineWidthAdjustment, yPos], {
            stroke: borderColor,
            strokeWidth: mobileView ? .5 : 1,
            selectable: false,
            evented: false
        });
        canvas.add(horizontalLine);
    }

    canvas.renderAll();
  }

  hideCanvas(element: HTMLElement, renderer: Renderer2) {
    renderer.setStyle(element, 'border', `0`);
    this.canvas.setWidth(0)
    this.canvas.setHeight(0)
  }

  /**
   * Delete object from canvas
   * @param target
   * @returns
   */
  deleteObject(imageUrl: string){
    const canvas = this.canvas;
    if(!canvas) return;
    const imageInCanvas: fabric.Image = this.canvas.getObjects('image').find((e: fabric.Image) => decodeURIComponent(e.getSrc()) == imageUrl) as fabric.Image
    this.objectDeleted.emit(imageUrl);
    canvas.remove(imageInCanvas);
    canvas.renderAll();
  }

  clearCanvas(){
    const canvas = this.canvas;
    if(!canvas) return;

    canvas.getObjects('image').forEach((img) => {
      canvas.remove(img);
    });
  }

  /**
   * Method that will add image to the canvas with given params
   * @param imageURL
   * @param left
   * @param top
   * @param scaleX
   * @param scaleY
   * @param style
   * @param cornerSize
   * @param color
   * @param dashArray
   * @param offset
   * @param padding
   * @returns
   */
  async addImageObject(imageInfo: PositionDesign, renderAll = true, canvas = this.canvas, forcedScaleTo?: string, resizeImage = false): Promise<{img: fabric.Image, imageInfo: PositionDesign}|null> {
    // if no canvas object is present we can't add any image
    if(!canvas) return new Promise((resolve, reject) => resolve(null));
    
    const imageWidth = forcedScaleTo == 'desktop' || !this.productImageClientWidth ? 700 : this.productImageClientWidth // we keep reference to the current product rendered image width (different than the actual image width)
    // if forcedScaleTo is set to desktop means we want to add the product without scaling in case we are in a mobile device
    // this option is specially important when exporting canvas or product mockups
    // 700px is the default fixed size for product images in desktop
    
    // we scale coordinates and scales based on the product image width
    const scaleY = imageWidth * imageInfo.scaleY / (imageInfo.imageClientWidth ?? 700)
    const scaleX = imageWidth * imageInfo.scaleX / (imageInfo.imageClientWidth ?? 700)
    const left = imageWidth * imageInfo.coordinates.x / (imageInfo.imageClientWidth ?? 700)
    const top = imageWidth * imageInfo.coordinates.y / (imageInfo.imageClientWidth ?? 700)

    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(imageInfo.url, (img) => {
        const imgOptions = {
          left,
          top,
          scaleX,
          scaleY,
          cornerStyle: imageInfo.cornerStyle,
          cornerSize: imageInfo.cornerSize,
          cornerColor: imageInfo.cornerColor,
          fill: imageInfo.fill,
          borderColor: imageInfo.borderColor,
          borderDashArray: [...imageInfo.borderDashArray],
          transparentCorners: false,
          // rotatingPointOffset: imageInfo.rotatingPointOffset,
          // cacheProperties: ['padding'],
          lockMovementX: imageInfo.editable ? false: true,
          lockMovementY:  imageInfo.editable ? false: true,
          lockRotation:  imageInfo.editable ? false: true,
          lockScalingX:  imageInfo.editable ? false: true,
          lockScalingY:  imageInfo.editable ? false: true,
          hasControls: imageInfo.editable,  // show control boxes
          selectable: imageInfo.editable,   // makes it selectable
          evented: imageInfo.editable,
          angle: imageInfo.rotation,
          originX: imageInfo.originX,
          originY: imageInfo.originX
        } as Partial<IObjectOptions>

        img.set(imgOptions);

        // I don't know what is this for, but it was causing an error on export mockup image, so I'm commenting it out and keeping it as reference
        // const width = img.width
        // const height = img.height
        // imageInfo.width = width
        // imageInfo.height = height
        // Resize the image in case it is required
        if (resizeImage) {
          this.resizeImageToFitCanvas(img, canvas);
          // I don't know what is this for, but it was causing an error on export mockup image, so I'm commenting it out and keeping it as reference
          // imageInfo.width = width
          // imageInfo.height = height
          // imageInfo.scaleX = img.scaleX
          // imageInfo.scaleY = img.scaleY
        }
        canvas.add(img);
        if (renderAll) {
          canvas.requestRenderAll();
        }
        resolve({img, imageInfo})
       }, {
        crossOrigin: 'anonymous'
      });
    })
  }

  exportCanvasAsImage(fabricCanvas, desiredWidth): Promise<string> {

    return new Promise(async (resolve, reject) => {
      // Temporarily store and remove non-image objects
        fabricCanvas.forEachObject((object) => {
            if (!(object instanceof fabric.Image)) {
                fabricCanvas.remove(object);
            }
        });

        // Export the canvas as an image
        const dataURL = fabricCanvas.toDataURL({
            format: 'png',
            quality: 1,
            multiplier: desiredWidth / fabricCanvas.getWidth(),
        });

        resolve(dataURL);
    });
  }

  async exportCanvasAsImageS3(designs: Designs, dimensions: DesignToolCanvas, variant: VariantColors, designMockups: VariantImages): Promise<MockupsUploadResponse[]> {
    const clientId = designs.clientId;
    const designId = designs._id;
    let uploadPromises: Promise<MockupsUploadResponse>[] = [];
    
    for (const position of designs.positions) {
      let dim = {...dimensions[position.position]};
      if (!dim?.left) {
        continue
      }
      const mobileView = await firstValueFrom(this.store.select(selectMobileView))
      dim.left = !mobileView ? dim.left : (700 * dim.left) / this.productImageClientWidth 
      dim.top = !mobileView ? dim.top : (700 * dim.top) / this.productImageClientWidth
      dim.width = !mobileView ? dim.width : (700 * dim.width) / this.productImageClientWidth 
      dim.height = !mobileView ? dim.height : (700 * dim.height) / this.productImageClientWidth 

      if (position?.canvas?.length > 0){
        const productImageUrl = designMockups?.[position.position] ?? variant?.images?.[position.position]
        const productImage = await this.loadImage(productImageUrl);
        productImage.style.height = 'auto'
        productImage.style.width = '700px'
        document.body.appendChild(productImage)
        // Combined canvas dimensions match the product image dimensions
        const mockupCanvas = document.createElement('canvas');
        mockupCanvas.width = 700;
        mockupCanvas.height = productImage.clientHeight;
        const mockupCanvasCtx = mockupCanvas.getContext('2d');

        // Draw the product image
        mockupCanvasCtx.drawImage(productImage, 0, 0, productImage.clientWidth, productImage.clientHeight);
        document.body.removeChild(productImage)

        // Draw overlay images
        const mockupArtOverlayCanvas = document.createElement('canvas');
        const mockupArtOverlayFabricCanvas = new fabric.Canvas(mockupArtOverlayCanvas, dim)
        const artCanvas = document.createElement('canvas');
        const artFabricCanvas = new fabric.Canvas(artCanvas, dim);

        // add each image to the canvas
        for (const img of position.canvas ) {
          await this.addImageObject(img, true, artFabricCanvas, 'desktop');
          await this.addImageObject(img, true, mockupArtOverlayFabricCanvas, 'desktop');
        }

        mockupCanvasCtx.drawImage(mockupArtOverlayFabricCanvas.toCanvasElement(), dim.left, dim.top, dim.width, dim.height);

        // Save the resulting canvas as an image
        const mockupFilename = `${position.position}-mockup.png`;
        const mockupDataUrl = mockupCanvas.toDataURL()
        const artFilename = `${position.position}-positionMockup.png`;
        const artDataUrl = await this.exportCanvasAsImage(artFabricCanvas, 3900);

        //conver to blob
        const artBlob = await fetch(artDataUrl).then(res => res.blob());
        const mockupBlob = await fetch(mockupDataUrl).then(res => res.blob());
        const formData = new FormData();
        formData.append('files[]', artBlob, artFilename);
        formData.append('files[]', mockupBlob, mockupFilename);
        formData.append('clientId', clientId.toString());
        formData.append('designId', designId.toString());

        const uploadPromise = new Promise<MockupsUploadResponse>((resolve, reject) => {
          this.catalogDesignService.uploadDesignPositionMockups(formData)
           .subscribe({
               next: (response: UploadDesignPositionMockupsResponse) => resolve({ position: position.position, url: response.artFile.Location, mockupUrl: response.mockupFile.Location } as MockupsUploadResponse),
               error: (error) => reject(error)
            })
        });
        uploadPromises.push(uploadPromise);
      }
    }

    const positionUrls = await Promise.all(uploadPromises);

    return positionUrls;
  }

  async exportProductMockup(designs: Designs, position: string, dimensions: DesignToolCanvas, variant: VariantColors, designMockups: VariantImages, renderer?: Renderer2): Promise<void> {
    // exporting result will always be based on desktop viewport size, whether the current viewport size is mobile or not
    let dim: Dimensions = {...dimensions[position]};

    if (!dim?.left) {
      return
    }
    
    const mobileView = await firstValueFrom(this.store.select(selectMobileView))
    dim.left = !mobileView ? dim.left : (700 * dim.left) / this.productImageClientWidth 
    dim.top = !mobileView ? dim.top : (700 * dim.top) / this.productImageClientWidth
    dim.width = !mobileView ? dim.width : (700 * dim.width) / this.productImageClientWidth 
    dim.height = !mobileView ? dim.height : (700 * dim.height) / this.productImageClientWidth 
    const _position = designs.positions.find(p => p.position === position)
    if (!_position) {
      return
    }
    if (_position?.canvas?.length > 0) {
      const productImageUrl = designMockups?.[position] ?? variant?.images?.[position]
      const productImage = await this.loadImage(productImageUrl);
      productImage.style.height = 'auto'
      productImage.style.width = '700px'
      document.body.appendChild(productImage)

      // Combined canvas dimensions match the product image dimensions
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = 700;
      combinedCanvas.height = productImage.clientHeight;
      const ctx = combinedCanvas.getContext('2d');

      // Draw the product image
      ctx.drawImage(productImage, 0, 0, productImage.clientWidth, productImage.clientHeight);
      document.body.removeChild(productImage)

      // Draw overlay images
      const overlayCanvas = document.createElement('canvas');
      const fabricOverlayCanvas = new fabric.Canvas(overlayCanvas, dim)
      
      for (const imageInfo of _position.canvas) {
        await this.addImageObject(imageInfo, true, fabricOverlayCanvas, 'desktop');
      }

      ctx.drawImage(fabricOverlayCanvas.toCanvasElement(), dim.left, dim.top, dim.width, dim.height);

      // Save the resulting canvas as an image
      const downloadLink = document.createElement('a');
      downloadLink.href = combinedCanvas.toDataURL();
      downloadLink.download = `${position}-mockup.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  }

  async loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        resolve(img)
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  getDataURL(imageUrl): Promise<string|ArrayBuffer|null> {
    // Use fetch to get the image as a blob
    return new Promise((resolve, reject) => {
      fetch(imageUrl)
      .then(response => response.blob())
      .then(blob => {
        // Convert the blob to a Data URL
        const reader = new FileReader();
        reader.onloadend = function() {
          const dataURL = reader.result;
          // You can use the dataURL as needed, e.g., save it, display it, etc.
          resolve(dataURL)
        };
        reader.readAsDataURL(blob);
      })
      .catch(error => {
        console.error('Error fetching image:', error)
        reject(null)
      });
    })
  }

  /**
   * Funtion to resize the image if it is bigger than the canvas
   * @param image
   * @param canvas
   */
  resizeImageToFitCanvas(image: fabric.Image, canvas: fabric.Canvas) {
    // Store the original position
    const originalLeft = image.left;
    const originalTop = image.top;

    // Check if the image has its default scaling (1 in this case)
    const requiresRescaling = image.scaleX < 1 || image.scaleY < 1;

    // If it has its default scaling, then proceed to adjust its size to fit the canvas
    if (requiresRescaling) {
        // Determine the scaling factors if the image were to fill the entire canvas
        const scaleX = canvas.width / image.width;
        const scaleY = canvas.height / image.height;

        // Take the minimum scale value to ensure the image fits both width and height
        let scale = Math.min(scaleX, scaleY);

        // Adjust the scale to make the image a bit smaller. This is optional.
        scale *= 0.5;

        // Apply the new scale to the image
        image.scale(scale);
    }

    // Set the image to its original position
    image.set({
      left: originalLeft,
      top: originalTop,
    });
  }

  /**
   * Method that will add the delete control to the canvas with specific icon
   * @param canvas
   */
  addDeleteControl(){
    const deleteIconImg = document.createElement('img');
    deleteIconImg.src = "assets/images/cancel.svg"

    fabric.Object.prototype.controls.deleteControl = new fabric.Control({
      actionName: 'Remove',
      x: 0,
      y: 0.5,
      offsetY: 30,
      offsetX: -30,
      sizeX: 24,
      sizeY: 24,
      cursorStyle: 'pointer',
      mouseUpHandler: (eventData, transformat, x, y) => {
        eventData.stopPropagation();
        this.deleteObject(decodeURIComponent((transformat.target as fabric.Image).getSrc()))
        return true;
    },
      render: (ctx, left, top, styleOverride, fabricObject) => {
        const size = 30;
        ctx.save();
        ctx.translate(left, top);
        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
        ctx.drawImage(deleteIconImg, -size/2, -size/2, size, size);
        ctx.restore();
      }
    });
  }

  /*resetObjectDeleted() {
    this.objectDeletedSubject.next({ deleted: false, src: null });
  }*/

  /**
   * Method that will add the rotation control to the canvas with specific icon
   * @param canvas
   */
  addRotationControl(){
    const rotationIconImg = document.createElement('img');
    rotationIconImg.src = 'assets/images/rotate.svg'

    fabric.Object.prototype.controls.mtr = new fabric.Control({
      actionName: 'Rotate',
      x: 0,
      y: 0.5,
      offsetY: 30,  // Adjust hit area
      offsetX: 30,
      sizeX: 24,
      sizeY: 24,
      cursorStyle: 'pointer',
      mouseDownHandler: this.startRotation.bind(this),
      actionHandler: this.rotateIcon.bind(this),
      mouseUpHandler: this.endRotation.bind(this),
      render: (ctx, left, top, styleOverride, fabricObject) => {
        const size = 30;  // Size of the icon. Adjust if necessary.
        ctx.save();
        ctx.translate(left, top);
        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
        ctx.drawImage(rotationIconImg, -(size/2), -(size/2), size, size);
        ctx.restore();
      }
    });
  }

  lockImagePosition(imageUrl){
    if(!this.canvas) return;

    const images = this.canvas.getObjects().filter(obj => obj.type === 'image') as fabric.Image[];
    const image = images.find(img => decodeURIComponent(img.getSrc()) === imageUrl);

    if (image) {
      image.set({
          lockMovementX: true,
          lockMovementY: true,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
          hasControls: false, // hides control boxes
          selectable: false,  // makes it non-selectable
          evented: false      // makes it non-interactive
      });
      this.canvas.renderAll();
      this.objectLocked.emit(imageUrl)
    }
  }

  editImagePosition(imageUrl){
    if(!this.canvas) return;

    const images = this.canvas.getObjects().filter(obj => obj.type === 'image') as fabric.Image[];
    const image = images.find(img => decodeURIComponent(img.getSrc()) === imageUrl);

    if(image){
      image.set({
        lockMovementX: false,
        lockMovementY: false,
        lockRotation: false,
        lockScalingX: false,
        lockScalingY: false,
        hasControls: true,  // show control boxes
        selectable: true,   // makes it selectable
        evented: true       // makes it interactive
      });
      this.canvas.renderAll();
      this.objectEdited.emit(imageUrl)
    }
  }

  /**
   * Following methods are used for rotation of the object and making it smooth
   * @param eventData
   * @param transform
   */
  private startRotation(eventData, transform) {
    const pointer = this.canvas.getPointer(eventData.e);
    const posX = pointer.x;
    const posY = pointer.y;
    const objectCenter = transform.target.getCenterPoint();

    const angleRadians = Math.atan2(posY - objectCenter.y, posX - objectCenter.x);
    this.initialAngle = fabric.util.radiansToDegrees(angleRadians);
    this.objectInitialAngle = transform.target.angle;

    // Set origin to center
    transform.target.set({
        originX: 'center',
        originY: 'center',
        left: objectCenter.x,
        top: objectCenter.y
    });

    this.rotating = true;
  }

  private rotateIcon(eventData, transform) {
    if (!this.rotating) {
        return;
    }

    const pointer = this.canvas.getPointer(eventData.e);
    const posX = pointer.x;
    const posY = pointer.y;
    const objectCenter = transform.target.getCenterPoint();

    const angleRadians = Math.atan2(posY - objectCenter.y, posX - objectCenter.x);
    const currentAngle = fabric.util.radiansToDegrees(angleRadians);
    const angleDifference = currentAngle - this.initialAngle;

    transform.target.set({
      originX: 'center',
      originY: 'center',
      angle: this.objectInitialAngle + angleDifference
    });

    this.canvas.renderAll();
  }

  private endRotation() {
    if (this.rotating) {
      this.rotating = false;

      // Reset to original values
      const target = this.canvas.getActiveObject();
      if (target && target.type === 'image') {
            //updated stored rotation angle
        this.rotationEnded.emit(target as fabric.Image);
      }
    }
  }

  addImageToCanvas(imageInfo: PositionDesign, renderAll = true, canvas = this.canvas, resizeImage = false): Promise<{img: fabric.Image, imageInfo: PositionDesign}|null> {
    if (!canvas) {
      console.warn('canvas not initialized/found')
      return new Promise((resolve, reject) => resolve(null))
    }
    try{
      const imageExists = canvas.getObjects().some(img => img instanceof fabric.Image && decodeURIComponent(img.getSrc()) === imageInfo.url);
      if (!imageExists) {
        return this.addImageObject(imageInfo, renderAll, canvas, undefined, resizeImage);
      }
    } catch(error) {
      console.error('Error in adding image:', error);
    }
    return new Promise((resolve, reject) => resolve(null))
  }

  disposeCanvas() {
    this.canvas?.dispose()
    this.canvas = null
  }
}
